import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import router from './router/index.js'
import App from './App.vue'
import { isNativeApp, initAppListeners, hideSplash, initPushNotifications } from './services/capacitor'

// Import CSS global - Design System e Mobile Layout
import '@/assets/css/variables.css'
import '@/assets/css/design-system.css'
import '@/assets/css/modal-fix.css'
import '@/assets/css/layout-fix.css'
import './style.css' // Tailwind utilities must be imported last so they override global helpers

// Force dark theme - always use dark mode regardless of system preferences
document.documentElement.setAttribute('data-theme', 'dark')

// Import Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css'

// Import Font Awesome CSS
import '@fortawesome/fontawesome-free/css/all.min.css'

import { setupInterceptors, setupDebugMiddleware, initApiServices } from './services/api/interceptors'
import { useAuthStore } from './stores/auth'

const pinia = createPinia()
pinia.use(createPersistedState({
  storage: localStorage,
  key: id => `bloop-${id}`
}))

const app = createApp(App)

app.use(pinia)
app.use(router)

// Setup API interceptors and services
setupInterceptors()
setupDebugMiddleware()

// Initialize API services (network monitoring, CSRF preload)
// Não await para não bloquear a inicialização do app
initApiServices().catch((err) => {
  if (import.meta.env.DEV) {
    console.warn('[API] Failed to initialize services:', err)
  }
})

// Initialize Capacitor for mobile apps
if (isNativeApp()) {
  initAppListeners(router)
  hideSplash()
  initPushNotifications(router)

  // DEBUG: Tentar obter o token FCM diretamente do plugin após alguns segundos
  setTimeout(async () => {
    console.log('[FCM DEBUG] Attempting to get FCM token from plugin...')
    try {
      const { FirebaseMessagingPlugin } = await import('./plugins/fcm')
      const result = await FirebaseMessagingPlugin.getFCMToken()
      console.log('[FCM DEBUG] Plugin result:', result)
      if (result?.token) {
        localStorage.setItem('fcm_token', result.token)
        console.log('[FCM DEBUG] Token saved to localStorage:', result.token)
        const authStore = useAuthStore()
        if (authStore?.token) {
          authStore.registerPushToken(result.token)
        }
      }
    } catch (err) {
      console.warn('[FCM DEBUG] Error getting token from plugin:', err)
    }
  }, 3000)
}

// Registrar token FCM do Firebase assim que for recebido
window.addEventListener('fcmTokenReady', (event: any) => {
  const fcmToken = event.detail.token
  console.log('[FCM] Token from Firebase:', fcmToken)
  localStorage.setItem('fcm_token', fcmToken)

  try {
    const authStore = useAuthStore()
    if (authStore?.token) {
      authStore.registerPushToken(fcmToken)
    }
  } catch (err) {
    console.warn('[FCM] Não foi possível registrar token recebido', err)
  }
})

// Fallback: Registrar token de push do Capacitor (APNs) - NOTA: este é APNs, não FCM
// O Firebase deve converter para FCM e disparar o evento 'fcmTokenReady' acima
window.addEventListener('pushTokenReceived', (event: any) => {
  try {
    const authStore = useAuthStore()
    console.log('[DEBUG] pushTokenReceived - authStore keys:', Object.keys(authStore))
    console.log('[DEBUG] pushTokenReceived - registerPushToken type:', typeof authStore.registerPushToken)

    if (!authStore?.token) return
    const detail = event?.detail || {}
    authStore.registerPushToken(detail.token, detail.deviceId)
  } catch (err) {
    console.warn('Não foi possível registrar token recebido', err)
  }
})

// Se já existe token FCM salvo e o usuário possui sessão, garantir registro logo ao iniciar
if (isNativeApp()) {
  const authStore = useAuthStore()
  if (authStore?.token && localStorage.getItem('fcm_token')) {
    authStore.registerPushToken().catch(() => {
      // silencioso
    })
  }
}

app.mount('#app')
